import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, defer, firstValueFrom } from 'rxjs';

import { AppEventBusService, AppEventType } from './app-event-bus.service';
import { CDRLesson } from '../models/CDRLesson';

import { environment } from '../../environments/environment';

interface CdrLessonsOptions {
  forceRefresh?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CDRLessonsService {
  private readonly http = inject(HttpClient);
  private readonly eventBus = inject(AppEventBusService);

  private cdrLessonsCache: CDRLesson[] = [];
  private isCacheValid = false;

  private readonly cdrLessonsSubject = new BehaviorSubject<CDRLesson[]>([]);

  public readonly cdrLessons$: Observable<CDRLesson[]> = defer(() => {
    if (!this.isCacheValid && !this.pendingRequest) {
      this.getCdrLessons();
    }
    return this.cdrLessonsSubject;
  });

  private pendingRequest: Promise<CDRLesson[]> | null = null;

  constructor() {
    this.eventBus.on(AppEventType.DataRefresh).subscribe(() => {
      this.invalidate();
      this.getCdrLessons({ forceRefresh: true });
    });
  }

  async getCdrLessons(options?: CdrLessonsOptions): Promise<CDRLesson[]> {
    if (!options?.forceRefresh && this.isCacheValid) {
      return this.cdrLessonsCache;
    }

    if (!options?.forceRefresh && this.pendingRequest) {
      return this.pendingRequest;
    }

    const request = this.fetchCDRLessons()
      .then((lessons) => {
        if (this.pendingRequest === request) {
          this.cdrLessonsCache = lessons;
          this.isCacheValid = true;
          this.cdrLessonsSubject.next(lessons);
        }
        return lessons;
      })
      .catch((error) => {
        console.error('CDRLessonsService: fetch failed', error);
        return this.cdrLessonsCache;
      })
      .finally(() => {
        if (this.pendingRequest === request) {
          this.pendingRequest = null;
        }
      });

    this.pendingRequest = request;
    return request;
  }

  invalidate(): void {
    this.isCacheValid = false;
  }

  private async fetchCDRLessons(): Promise<CDRLesson[]> {
    const url = `${environment.token_auth_config.apiBase}/v${environment.apiVersion}/cdr_lessons`;
    const data = await firstValueFrom(this.http.get(url));
    return this.buildCDRLessonsFromRawData(data);
  }

  private buildCDRLessonsFromRawData(data: unknown): CDRLesson[] {
    interface CdrLessonRaw {
      id: number;
      type: string;
      attributes: {
        name: string;
        slug: string;
        theme: string;
        subtheme: string;
        paragraphs: CDRLesson['paragraphs'];
      };
    }
    const body = data as { data: CdrLessonRaw[] } | null;

    if (!body?.data) {
      return [];
    }

    const arr: CDRLesson[] = [];
    const themeRanks: Record<string, number> = {
      signalisation: 0,
      circulation: 1,
      route: 2,
      'autres usagers': 3,
      conducteur: 4,
      divers: 5,
      'prendre/quitter le véhicule': 6,
      'sécurité passagers/véhicule': 7,
      'mécanique/équipement': 8,
      environnement: 9,
      'accidents/secours': 10,
    };

    for (const elt of body.data) {
      const theme = elt.attributes.theme.toLowerCase();
      const CDRRank = themeRanks[theme] ?? -1;

      arr.push({
        remoteId: elt.id,
        type: elt.type,
        name: elt.attributes.name,
        slug: elt.attributes.slug,
        theme,
        subtheme: elt.attributes.subtheme,
        paragraphs: elt.attributes.paragraphs,
        rank: CDRRank,
      });
    }
    return arr;
  }
}
